import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";
import { Table } from "@tanstack/react-table";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { useState, useEffect, useMemo } from "react";
import { globalFullscreenState } from "@/components/fullscreen-sidebar";
import { useTableDefaultPageSize } from "@/hooks/use-table-default-page-size";

interface PaginationProps<TData> {
  table: Table<TData>;
  isSidebar?: boolean;
  onDefaultReset?: (newDefaultSize: number) => void;
  onPageSizeChange?: (newSize: number) => void;
}

export function Pagination<TData>({
  table,
  isSidebar = false,
  onDefaultReset,
  onPageSizeChange,
}: PaginationProps<TData>) {
  const {
    getState,
    setPageIndex,
    setPageSize,
    setPagination,
    getPageCount,
    getCanPreviousPage,
    getCanNextPage,
    previousPage,
    nextPage,
  } = table;

  const isMobile = useIsMobile();
  const defaultPageSize = useTableDefaultPageSize();
  const [isTablet, setIsTablet] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState(() => globalFullscreenState.isOpen);

  useEffect(() => {
    if (!isSidebar) return;
    const unsubscribe = globalFullscreenState.subscribe(() => {
      setIsFullscreen(globalFullscreenState.isOpen);
    });
    return unsubscribe;
  }, [isSidebar]);

  useEffect(() => {
    const checkTablet = () => {
      setIsTablet(window.innerWidth >= 768 && window.innerWidth < 1024);
    };

    checkTablet();
    window.addEventListener("resize", checkTablet);
    return () => window.removeEventListener("resize", checkTablet);
  }, []);

  const { pagination } = getState();
  const currentPage = pagination.pageIndex + 1;
  const totalPages = getPageCount();
  const rowsPerPage = pagination.pageSize;
  const totalRows = table.getFilteredRowModel().rows.length;

  const rowsPerPageOptions = useMemo(() => {
    const baseOptions = [5, 10, 20, 50].filter((option) => option !== defaultPageSize);
    const isDefault = rowsPerPage === defaultPageSize;

    if (!baseOptions.includes(rowsPerPage) && !isDefault) {
      return [...baseOptions, rowsPerPage].sort((a, b) => a - b);
    }
    return baseOptions;
  }, [rowsPerPage, defaultPageSize]);

  const handleRowsPerPageChange = (value: string) => {
    if (value === "default") {
      setPagination({ pageIndex: 0, pageSize: defaultPageSize });
      onDefaultReset?.(defaultPageSize);
    } else {
      const size = Number(value);
      setPageSize(size);
      onPageSizeChange?.(size);
    }
  };

  useEffect(() => {
    if (isMobile === true && rowsPerPage !== defaultPageSize) {
      setPageSize(defaultPageSize);
    }
  }, [isMobile, defaultPageSize, rowsPerPage, setPageSize]);

  // Don't render until mobile detection is complete
  if (isMobile === undefined) {
    return (
      <div className="flex items-center justify-between px-2 mt-3">
        <div className="h-6 w-32 bg-muted animate-pulse rounded"></div>
        <div className="flex items-center gap-2">
          <div className="h-8 w-24 bg-muted animate-pulse rounded"></div>
          <div className="h-8 w-32 bg-muted animate-pulse rounded"></div>
        </div>
      </div>
    );
  }

  // Sidebar compact layout — no viewport-based responsive classes so it stays
  // compact regardless of how wide the browser window is.
  if (isSidebar && !isFullscreen) {
    return (
      <div className="flex flex-col gap-2 px-2 mt-3">
        <div className="flex items-center justify-center gap-1.5">
          <button
            type="button"
            className="p-1.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50"
            onClick={(e) => {
              e.preventDefault();
              setPageIndex(0);
            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft size={16} />
          </button>
          <button
            type="button"
            className="p-1.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50"
            onClick={(e) => {
              e.preventDefault();
              previousPage();
            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft size={16} />
          </button>
          <div className="flex items-center gap-1 text-sm font-medium px-2">
            <span>{currentPage}</span>
            <span className="text-muted-foreground">of</span>
            <span>{totalPages}</span>
          </div>
          <button
            type="button"
            className="p-1.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50"
            onClick={(e) => {
              e.preventDefault();
              nextPage();
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight size={16} />
          </button>
          <button
            type="button"
            className="p-1.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50"
            onClick={(e) => {
              e.preventDefault();
              setPageIndex(totalPages - 1);
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const useMobileStyle = isMobile === true || isTablet;

  if (useMobileStyle) {
    return (
      <div className="flex flex-col gap-3 px-2 mt-3 md:flex-row md:items-center md:justify-between">
        {isTablet && (
          <div className="text-sm text-muted-foreground">
            {`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
              currentPage * rowsPerPage,
              totalRows
            )} of ${totalRows} rows`}
          </div>
        )}

        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-4">
          {isTablet && (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">Rows per page</span>
              <Select
                value={rowsPerPage === defaultPageSize ? "default" : String(rowsPerPage)}
                onValueChange={handleRowsPerPageChange}
              >
                <SelectTrigger className="h-8 w-17.5 text-sm">
                  <SelectValue>{rowsPerPage}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Default ({defaultPageSize})</SelectItem>
                  {rowsPerPageOptions.map((option) => (
                    <SelectItem key={option} value={String(option)}>
                      {option}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Navigation controls - consistent for both mobile and tablet */}
          <div className="flex items-center justify-center gap-2">
            {/* First page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 
                ${isMobile === true ? "p-2 touch-manipulation" : "p-1.5"}`}
              onClick={(e) => {
                e.preventDefault();
                setPageIndex(0);
              }}
              disabled={!getCanPreviousPage()}
            >
              <span className="sr-only">Go to first page</span>
              <ChevronsLeft size={isMobile === true ? 18 : 16} />
            </button>

            {/* Previous page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 
                ${isMobile === true ? "p-2 touch-manipulation" : "p-1.5"}`}
              onClick={(e) => {
                e.preventDefault();
                previousPage();
              }}
              disabled={!getCanPreviousPage()}
            >
              <span className="sr-only">Go to previous page</span>
              <ChevronLeft size={isMobile === true ? 18 : 16} />
            </button>

            {/* Current page indicator */}
            <div className="flex items-center gap-2 text-sm font-medium min-w-20 justify-center">
              <span>{currentPage}</span>
              <span className="text-muted-foreground">of</span>
              <span>{totalPages}</span>
            </div>

            {/* Next page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 
                ${isMobile === true ? "p-2 touch-manipulation" : "p-1.5"}`}
              onClick={(e) => {
                e.preventDefault();
                nextPage();
              }}
              disabled={!getCanNextPage()}
            >
              <span className="sr-only">Go to next page</span>
              <ChevronRight size={isMobile === true ? 18 : 16} />
            </button>

            {/* Last page button */}
            <button
              type="button"
              className={`border border-gray-300 rounded disabled:opacity-50 cursor-pointer hover:bg-gray-50 
                ${isMobile === true ? "p-2 touch-manipulation" : "p-1.5"}`}
              onClick={(e) => {
                e.preventDefault();
                setPageIndex(totalPages - 1);
              }}
              disabled={!getCanNextPage()}
            >
              <span className="sr-only">Go to last page</span>
              <ChevronsRight size={isMobile === true ? 18 : 16} />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop layout (original)
  return (
    <div className="flex items-center justify-between px-2 mt-3">
      <div className="flex-1 text-sm text-muted-foreground">
        {`${(currentPage - 1) * rowsPerPage + 1}-${Math.min(
          currentPage * rowsPerPage,
          totalRows
        )} of ${totalRows} rows`}
      </div>
      <div className="flex items-center space-x-6 lg:space-x-8">
        <div className="flex items-center space-x-2">
          <p className="text-sm font-medium">Rows per page</p>
          <Select
            value={rowsPerPage === defaultPageSize ? "default" : String(rowsPerPage)}
            onValueChange={handleRowsPerPageChange}
          >
            <SelectTrigger className="h-8 w-17.5 text-sm font-medium border border-gray-300 rounded px-2 py-1">
              <SelectValue>{rowsPerPage}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="default">Default ({defaultPageSize})</SelectItem>
              {rowsPerPageOptions.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex w-25 items-center justify-center text-sm font-medium">
          Page {currentPage} of {totalPages}
        </div>
        <div className="flex items-center space-x-2">
          <button
            type="button"
            className="hidden p-0.5 lg:flex border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setPageIndex(0);
            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">Go to first page</span>
            <ChevronsLeft />
          </button>
          <button
            type="button"
            className="p-0.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              previousPage();
            }}
            disabled={!getCanPreviousPage()}
          >
            <span className="sr-only">Go to previous page</span>
            <ChevronLeft />
          </button>
          <button
            type="button"
            className="p-0.5 border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              nextPage();
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">Go to next page</span>
            <ChevronRight />
          </button>
          <button
            type="button"
            className="hidden p-0.5 lg:flex border border-gray-300 rounded disabled:opacity-50 cursor-pointer"
            onClick={(e) => {
              e.preventDefault();
              setPageIndex(totalPages - 1);
            }}
            disabled={!getCanNextPage()}
          >
            <span className="sr-only">Go to last page</span>
            <ChevronsRight />
          </button>
        </div>
      </div>
    </div>
  );
}
